import { useEffect, useState } from 'react'
import {
  getHuddlePatientMetrics,
  getHuddlePatientMetricsOverview,
} from '@services/api/dashboard/huddle.api'
import type { DashboardMetricItemData } from '../../types/dashboardMetrics.types'
import { getHuddleTodayPatientsMetrics } from '@components/pages/Dashboard/HuddleTab/huddleMockData'

export type HuddlePatientMetricsState = {
  metrics: DashboardMetricItemData[]
  data: DashboardMetricItemData[] | null
  loading: boolean
  error: string | null
}

export type UseHuddlePatientMetricsOptions = {
  date?: string
  enabled?: boolean
  queued?: boolean
}

type PatientMetricsFetcher = (
  date?: string,
) => Promise<DashboardMetricItemData[]>

/**
 * Loads the Huddle → Patients metrics from the backend.
 *
 * While loading or on error it falls back to the static placeholder values.
 * `data` is the raw API payload (null until a successful response). `date`
 * (YYYY-MM-DD) is optional and pins the "today" anchor server-side.
 */
function useHuddlePatientMetricsFromFetcher(
  dateOrOptions: string | UseHuddlePatientMetricsOptions | undefined,
  fetcher: PatientMetricsFetcher,
): HuddlePatientMetricsState {
  const options: UseHuddlePatientMetricsOptions =
    typeof dateOrOptions === 'string' || dateOrOptions === undefined
      ? { date: dateOrOptions }
      : dateOrOptions
  const { date, enabled = true, queued = false } = options
  const waiting = queued && !enabled

  const [data, setData] = useState<DashboardMetricItemData[] | null>(null)
  const [loading, setLoading] = useState(enabled || waiting)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled) {
      setLoading(waiting)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)

    fetcher(date)
      .then((result) => {
        if (!cancelled) setData(result)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(
          e instanceof Error ? e.message : 'Failed to load patient metrics',
        )
        setData(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [date, enabled, fetcher, waiting])

  return {
    metrics: data ?? getHuddleTodayPatientsMetrics(),
    data,
    loading: loading || waiting,
    error,
  }
}

export function useHuddlePatientMetrics(
  dateOrOptions?: string | UseHuddlePatientMetricsOptions,
): HuddlePatientMetricsState {
  return useHuddlePatientMetricsFromFetcher(
    dateOrOptions,
    getHuddlePatientMetrics,
  )
}
export function useHuddlePatientMetricsOverview(
  dateOrOptions?: string | UseHuddlePatientMetricsOptions,
): HuddlePatientMetricsState {
  return useHuddlePatientMetricsFromFetcher(
    dateOrOptions,
    getHuddlePatientMetricsOverview,
  )
}
